import { useState } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@nucleus-commerce/ui/components/form';
import { Input } from '@nucleus-commerce/ui/components/input';
import { cn } from '@nucleus-commerce/ui/lib/utils';

import { MediaRichTextEditor } from '@/features/media';
import { ScopeChip } from '@/features/products';
import { stripHtml } from '@/features/seo';

import type { StoreProductFormInput } from '../schemas/store-product-form-schema';

type OverridePath = 'title' | 'subtitle' | 'descriptionHtml';

export interface OverrideFieldProps {
  name: OverridePath;
  label: string;
  /** The value this store inherits; null when the master has none either. */
  master: string | null | undefined;
  /** The value is HTML: edited in the rich text editor, the master shown as plain text. */
  html?: boolean;
  /** Opens on the input with nothing to inherit: the card's "Add a subtitle". */
  startEditing?: boolean;
  /** The input lost focus or went back to the master. */
  onRelease?: () => void;
  /** Changes when the page goes back to a clean state, so an open override closes. */
  settled: number;
  placeholder?: string;
  className?: string;
}

/**
 * A text the store inherits until it says otherwise. Inherited shows the master value as text;
 * Override opens the input holding that value, because a change is usually a tweak, and the form
 * hears of it only at the first edit, so nothing is dirty before that. A blank field means inherit,
 * so an input left blank or untouched goes back to Inherited once it loses focus, and an override
 * still equal to the master is saved as none (`toStoreProductUpdate`), so it keeps following the
 * master. Use master clears it. Without the right to write it's the same text and no button.
 */
export function OverrideField({
  name,
  label,
  master,
  html = false,
  startEditing = false,
  onRelease,
  settled,
  placeholder,
  className,
}: OverrideFieldProps) {
  const { t } = useTranslation('storeCatalog');
  const form = useFormContext<StoreProductFormInput>();
  // True while the input must stay although it's blank: focused, or just opened on nothing.
  const [editing, setEditing] = useState(startEditing);
  const [focusOnMount, setFocusOnMount] = useState(startEditing);
  // The input shows the master's text and the form still holds blank: no edit yet.
  const [untouched, setUntouched] = useState(false);
  const [seenSettled, setSeenSettled] = useState(settled);
  if (settled !== seenSettled) {
    setSeenSettled(settled);
    setEditing(false);
    setUntouched(false);
  }
  const readOnly = form.formState.disabled;
  const toText = (value: string | null | undefined) => (value && html ? stripHtml(value) : value);
  const masterText = toText(master);

  return (
    <FormField
      control={form.control}
      name={name}
      render={({ field }) => {
        const own = editing || untouched || field.value.trim() !== '';
        const shown = own ? toText(field.value) : masterText;
        const nothing = !shown;
        const change = (next: string) => {
          setUntouched(false);
          field.onChange(next);
        };
        return (
          <FormItem className={className}>
            <div className="flex items-center gap-2">
              {own && !readOnly ? (
                <FormLabel>{label}</FormLabel>
              ) : (
                <span className="text-sm leading-none font-medium">{label}</span>
              )}
              {own ? (
                <ScopeChip scope="store" />
              ) : (
                <span className="bg-muted text-muted-foreground rounded-full px-2 py-0.5 text-xs whitespace-nowrap">
                  {t('storePage.override.inherited')}
                </span>
              )}
              <span className="flex-1" />
              {readOnly ? null : (
                <Button
                  type="button"
                  variant="link"
                  size="xs"
                  className="text-primary h-auto p-0 text-[13px] font-normal"
                  aria-label={
                    own
                      ? t('storePage.override.useMasterFor', { field: label })
                      : t('storePage.override.overrideFor', { field: label })
                  }
                  // Focus stays put: a blur here would swap this button's meaning under the click.
                  onMouseDown={(event) => event.preventDefault()}
                  onClick={() => {
                    if (own) {
                      field.onChange('');
                      setEditing(false);
                      setUntouched(false);
                      onRelease?.();
                    } else {
                      setUntouched(Boolean(master));
                      setEditing(!master);
                      setFocusOnMount(true);
                    }
                  }}
                >
                  {own ? t('storePage.override.useMaster') : t('storePage.override.override')}
                </Button>
              )}
            </div>
            {own && !readOnly ? (
              <div
                onFocus={() => setEditing(true)}
                onBlur={(event) => {
                  if (event.currentTarget.contains(event.relatedTarget)) return;
                  // The editor's own dialogs take focus away; it must not close under them.
                  if (!html) {
                    setEditing(false);
                    setUntouched(false);
                  }
                  onRelease?.();
                }}
              >
                <FormControl>
                  {html ? (
                    <MediaRichTextEditor
                      value={untouched ? (master ?? '') : field.value}
                      onChange={(next) => change(next ?? '')}
                      onBlur={field.onBlur}
                      aria-label={label}
                    />
                  ) : (
                    <Input
                      {...field}
                      value={untouched ? (master ?? '') : field.value}
                      onChange={(event) => change(event.target.value)}
                      autoFocus={focusOnMount}
                      placeholder={placeholder}
                    />
                  )}
                </FormControl>
              </div>
            ) : (
              <p
                className={cn(
                  'rounded-md border px-3 py-2 text-sm break-words',
                  html && 'whitespace-pre-line',
                  !own && 'bg-muted/40 text-muted-foreground',
                )}
              >
                {nothing ? t('storePage.override.masterEmpty') : shown}
              </p>
            )}
            <FormMessage />
          </FormItem>
        );
      }}
    />
  );
}
